import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react';
import {runInAction} from 'mobx';
import s from '../s';
import * as emitter from '../../global/emitter';
import DescMainContent from '../components/description-main/desc-main-content';
import Yotpo from '../components/yotpo';
import RelatedProducts from '../components/related-products/related-products';
import PriceSummaryPanel from '../components/price-summary';
import DocSizeDiffModal from '../components/doc-sizes-and-categories/doc-size-diff-modal';
import GlassInfoModals from '../components/glass-info-modals';
import {BackorderNotice, PromoBlock, StoreH2Content, StoreProductMessageBlock} from '../components/store-blocks';
import {getCustomerPhotosPlacement, shouldShowCustomerPhotos} from '../components/slider/customer-photos/customer-photos-util';
import {getTitle} from '../components/insignia/insignia';
import {screenResizeStore} from '../../global/store/screen-resize-store';
import {setPreviewScroll} from '../selection/preview-scroll';
import {linoSectionShown, prepareLinos} from '../selection/lino';
import {initInsigniaSelection, insigniaLinoChanged, insigniaSectionMounted, insigniaSectionShown, resetInsigniaView} from '../selection/insignia';
import {initGradYearInput, gradYearMounted} from '../selection/grad-year';
import {mouldingSectionShown} from '../selection/moulding';
import {matSectionShown} from '../selection/mat';
import {glassSectionShown, isAcrylicGlazing} from '../selection/glass';
import Overview, {CustomerPhotos} from './overview';
import Stepper from './steps';
import {ShipLine, TrustLine} from './atc-row';
import PinnedBar from './pinned-bar';
import {redesign} from './redesign-store';
import {stepPlan} from './steps-plan';
import {picksSummary} from './copy';
import {isRedesignTwoColumn} from './viewport';

/**
 * What the control's option components do when they first render, done once before the redesign's first render so the product
 * state starts the same in both layouts: the lino titles and order, the seal list's own setup (which applies the product's seal),
 * the graduation year field's value. The redesign keeps the shopper in its steps (no scroll back to the frame) and shows the Fast
 * Ship change in its ship line (no "Fast Shipping Removed" pop-up).
 */
const initRedesign = (): void => {
    setPreviewScroll(false);
    runInAction(() => {
        s.fast_ship_notice_inline = true;
    });
    if (linoSectionShown()) {
        prepareLinos();
    }
    if (insigniaSectionMounted()) {
        resetInsigniaView();
        if (insigniaSectionShown()) {
            initInsigniaSelection();
        }
    }
    if (gradYearMounted()) {
        initGradYearInput();
    }
    redesign.reset();
};

/** The picks in words: school, frame, mat, seal, glass (the ones the page offers). */
const summaryOfPicks = (): string => {
    const lino = linoSectionShown() && s.ps.lino_id > 0 ? (s.linoChoices.options.find(m => m.id === s.preferredLinoId(s.ps.lino_id))?.title ?? s.ps.lino_text) : '';
    const glass = glassSectionShown() ? s.glass.find(g => g.id === s.ps.glass_id)?.title : '';
    return picksSummary([lino, mouldingSectionShown() ? s.ui.title_moulding : '', matSectionShown() && s.ui.title_mat_combo ? `${s.ui.title_mat_combo} mat` : '', insigniaSectionShown() && s.ps.insignia_id > 0 ? getTitle() : '', glass ? `${glass} ${isAcrylicGlazing() ? 'acrylic' : 'glass'}` : '']);
};

/**
 * The step-by-step product page (the B arm of q4_2026_product_page_3, WEB-1213): the overview column, the steps column, "About this
 * frame", reviews, "We also recommend", the store content and the pinned bar. It renders over the control's store and components;
 * every option change goes through the shared selection handlers (src/product/selection/). All styles sit under .pp3 in
 * redesign.scss, a stylesheet DF4 inlines only on B pages. Its blocks are divs with ARIA roles, not sections: the site's
 * section p rules (_prismic.scss: padding, underlined links) would reach B's text and the control components inside it.
 */
const LayoutB: React.FC = observer(() => {
    useState(initRedesign);

    // The seal list re-picks its seal after a school change, as the control's Insignia does while it is mounted.
    useEffect(() => {
        if (!insigniaSectionMounted()) return;
        emitter.eventEmitter.subscribe(emitter.EventType.AB, 'lino_changed', insigniaLinoChanged);
        return () => emitter.eventEmitter.unsubscribe(emitter.EventType.AB, 'lino_changed');
    }, []);

    const plan = stepPlan();
    const summary = summaryOfPicks();
    const twoColumn = isRedesignTwoColumn(screenResizeStore.width);
    const photos =
        getCustomerPhotosPlacement(
            shouldShowCustomerPhotos({
                isPortal3: s.IsPortal3,
                allowCustomerPhotos: s.ui.allow_customer_photos,
                showCafDocSizes: s.ui.show_caf_doc_sizes,
                isCaf: s.ps.is_caf,
                ready: s.ready,
                realImageCount: s.prod?.real_images?.length ?? 0,
            }),
            screenResizeStore.width,
        ) !== null;

    return (
        <div
            className="pp3"
            data-layout="b"
        >
            <div className="pp3-main">
                <Overview photos={photos && twoColumn} />
                <div
                    className="pp3-steps"
                    role="region"
                    aria-labelledby="pp3-steps-title"
                >
                    <BackorderNotice />
                    <Stepper
                        plan={plan}
                        summary={summary}
                    />
                    <ShipLine />
                    <TrustLine />
                    {s.ui.price_summary && <PriceSummaryPanel />}
                    <StoreProductMessageBlock />
                    <PromoBlock />
                    {photos && !twoColumn && <CustomerPhotos />}
                </div>
            </div>
            <div
                className="pp3-about"
                role="region"
                aria-labelledby="pp3-about-title"
            >
                <h2 id="pp3-about-title">About this frame</h2>
                <div className="pp3-about-body">
                    <DescMainContent />
                </div>
            </div>
            <div className="pp3-below">
                {s.ui.allow_reviews && <Yotpo />}
                <RelatedProducts />
                <StoreH2Content />
            </div>
            <PinnedBar summary={summary} />
            <DocSizeDiffModal />
            <GlassInfoModals />
        </div>
    );
});

export default LayoutB;
